import { API_BASE_URL } from '@/config/api';
import { buildLocalizedPath } from '@/lib/routing';

const TENANT_APP_ORIGIN_STORAGE_KEY = 'carnitrack.tenantAppOrigin';
const TENANT_SLUG_STORAGE_KEY = 'carnitrack.tenantSlug';

/**
 * Optional rewrite for post-login redirects when the API still points at the CRA port (3000)
 * but you intentionally run this SPA on another port in local dev.
 *
 * Guardrail: only rewrite when the current browser tab is already using the configured port.
 * That prevents stale env values from accidentally sending users to the Django host on :8000.
 */
export function resolvePostLoginRedirectUrl(href) {
  if (!href || typeof href !== 'string') return href;
  const port = process.env.REACT_APP_POST_LOGIN_PORT;
  if (!port || typeof window === 'undefined') return href;

  try {
    const current = new URL(window.location.href);
    if (current.port !== String(port)) {
      return href;
    }

    const u = new URL(href, window.location.origin);
    if (u.hostname !== 'localhost' && !u.hostname.endsWith('.localhost')) {
      return href;
    }
    u.port = String(port);
    return u.toString();
  } catch {
    return href;
  }
}

function parseOriginCandidate(value) {
  if (!value) return '';

  try {
    return new URL(String(value).trim()).origin;
  } catch {
    return '';
  }
}

function isBareLocalhostHost(hostname) {
  const value = String(hostname || '').trim().toLowerCase();
  return value === 'localhost' || value === '127.0.0.1';
}

function parseHostCandidate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    return new URL(raw).host;
  } catch {
    return raw
      .replace(/^[a-z]+:\/\//i, '')
      .replace(/\/.*$/, '')
      .trim();
  }
}

function isLocalHost(host) {
  const hostname = String(host || '').split(':')[0];
  return hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '127.0.0.1';
}

function getConfiguredLocalApiOrigin() {
  const origin = parseOriginCandidate(API_BASE_URL);
  if (!origin) return '';

  try {
    const u = new URL(origin);
    return isBareLocalhostHost(u.hostname) ? u.origin : '';
  } catch {
    return '';
  }
}

export function shouldUseLocalDevTenantFallback() {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return false;
  return isBareLocalhostHost(window.location.hostname) && Boolean(getConfiguredLocalApiOrigin());
}

export function getLocalDevTenantApiOrigin() {
  return shouldUseLocalDevTenantFallback() ? getConfiguredLocalApiOrigin() : '';
}

export function getLocalDevTenantSpaOrigin() {
  return shouldUseLocalDevTenantFallback() ? window.location.origin : '';
}

function shouldRewriteToCurrentLocalhost(hostname) {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return false;

  const currentHostname = String(window.location.hostname || '').trim();
  if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
    return false;
  }

  const targetHostname = String(hostname || '').trim();
  return targetHostname.endsWith('.localhost');
}

function rewriteHostToCurrentLocalhost(host) {
  if (!host || typeof window === 'undefined') return host;

  const [hostname, ...rest] = String(host).split(':');
  if (!shouldRewriteToCurrentLocalhost(hostname)) {
    return host;
  }

  const port = rest.length ? `:${rest.join(':')}` : '';
  return `${window.location.hostname}${port}`;
}

export function rewriteDevLocalhostUrl(href) {
  if (!href || typeof href !== 'string' || typeof window === 'undefined') return href;

  try {
    const u = new URL(href, window.location.origin);
    if (!shouldRewriteToCurrentLocalhost(u.hostname)) {
      return href;
    }
    u.hostname = window.location.hostname;
    return u.toString();
  } catch {
    return href;
  }
}

function rewriteUrlToOrigin(href, origin) {
  if (!href || !origin || typeof window === 'undefined') return href;

  try {
    const target = new URL(String(href), window.location.origin);
    const preferred = new URL(origin);
    target.protocol = preferred.protocol;
    target.host = preferred.host;
    return target.toString();
  } catch {
    return href;
  }
}

function resolveLocalDevPreferredOrigin(targetUrl) {
  const apiOrigin = getLocalDevTenantApiOrigin();
  const spaOrigin = getLocalDevTenantSpaOrigin();
  if (!apiOrigin && !spaOrigin) return '';

  const explicitPort = targetUrl.port || '';
  if (apiOrigin) {
    const apiPort = new URL(apiOrigin).port || '';
    if (explicitPort && explicitPort === apiPort) {
      return apiOrigin;
    }
  }

  if (spaOrigin) {
    const spaPort = new URL(spaOrigin).port || '';
    if (explicitPort && explicitPort === spaPort) {
      return spaOrigin;
    }
  }

  return targetUrl.pathname.startsWith('/api/') ? apiOrigin : spaOrigin;
}

function buildOriginFromHost(host) {
  if (!host || typeof window === 'undefined') return '';
  const protocol = isLocalHost(host) ? window.location.protocol : 'https:';
  return `${protocol}//${rewriteHostToCurrentLocalhost(host)}`;
}

function normalizeStoredOrigin(value) {
  return String(value || '').trim().replace(/\/$/, '');
}

function readStorage(key) {
  if (typeof window === 'undefined') return '';

  try {
    return String(window.localStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
}

function writeStorage(key, value) {
  if (typeof window === 'undefined') return;

  try {
    if (!value) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, String(value));
  } catch {
    /* ignore quota / private mode */
  }
}

function hasDistinctSubdomain(host) {
  const hostname = String(host || '').split(':')[0].replace(/^www\./i, '');
  return hostname.split('.').filter(Boolean).length > 2;
}

export function getTenantSlug(tenant) {
  const candidates = [
    tenant?.slug,
    tenant?.tenant_slug,
    tenant?.workspace_slug,
    tenant?.schema_name,
    tenant?.schema,
    tenant?.subdomain,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (value) return value;
  }

  return '';
}

function hostStartsWithSlug(host, slug) {
  return !!(host && slug && host.toLowerCase().startsWith(`${slug.toLowerCase()}.`));
}

function deriveTenantHostFromSlug(slug) {
  if (!slug || typeof window === 'undefined') return '';

  const currentHost = window.location.hostname;
  if (!currentHost) return '';

  if (isLocalHost(currentHost)) {
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${slug}.localhost${port}`;
  }

  const labels = currentHost.replace(/^www\./i, '').split('.').filter(Boolean);
  if (labels.length < 2) return '';

  const baseDomain = labels.slice(-2).join('.');
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${slug}.${baseDomain}${port}`;
}

export function resolveTenantAppOrigin(tenant) {
  const localDevSpaOrigin = getLocalDevTenantSpaOrigin();
  if (localDevSpaOrigin) {
    return localDevSpaOrigin;
  }

  const slug = getTenantSlug(tenant);
  const primaryDomainHost = parseHostCandidate(tenant?.primary_domain);
  const authLoginOrigin = parseOriginCandidate(tenant?.auth_login_url);
  const authLoginHost = parseHostCandidate(tenant?.auth_login_url);
  const apiBaseOrigin = parseOriginCandidate(tenant?.api_base_url);
  const apiBaseHost = parseHostCandidate(tenant?.api_base_url);
  const webAppOrigin = parseOriginCandidate(tenant?.web_app_base_url);
  const webAppHost = parseHostCandidate(tenant?.web_app_base_url);
  const postLoginOrigin = parseOriginCandidate(tenant?.post_login_redirect_url);
  const postLoginHost = parseHostCandidate(tenant?.post_login_redirect_url);

  if (slug && hostStartsWithSlug(primaryDomainHost, slug)) {
    return buildOriginFromHost(primaryDomainHost);
  }

  if (slug && hostStartsWithSlug(authLoginHost, slug)) {
    return buildOriginFromHost(authLoginHost);
  }

  if (slug && hostStartsWithSlug(apiBaseHost, slug)) {
    return buildOriginFromHost(apiBaseHost);
  }

  if (slug && hostStartsWithSlug(webAppHost, slug)) {
    return buildOriginFromHost(webAppHost);
  }

  if (slug && hostStartsWithSlug(postLoginHost, slug)) {
    return buildOriginFromHost(postLoginHost);
  }

  if (slug) {
    const derivedHost = deriveTenantHostFromSlug(slug);
    if (derivedHost) {
      return buildOriginFromHost(derivedHost);
    }
  }

  if (primaryDomainHost) {
    return buildOriginFromHost(primaryDomainHost);
  }

  if (authLoginOrigin) {
    return authLoginOrigin;
  }

  if (apiBaseOrigin) {
    return apiBaseOrigin;
  }

  return webAppOrigin || postLoginOrigin || '';
}

export function getStoredTenantSlug() {
  return readStorage(TENANT_SLUG_STORAGE_KEY);
}

export function getStoredTenantAppOrigin() {
  const localDevSpaOrigin = getLocalDevTenantSpaOrigin();
  if (localDevSpaOrigin) return localDevSpaOrigin;

  const storedOrigin = normalizeStoredOrigin(
    rewriteDevLocalhostUrl(readStorage(TENANT_APP_ORIGIN_STORAGE_KEY)),
  );
  if (storedOrigin) return storedOrigin;

  const slug = getStoredTenantSlug();
  if (!slug) return '';

  const derivedHost = deriveTenantHostFromSlug(slug);
  return derivedHost ? buildOriginFromHost(derivedHost) : '';
}

export function setStoredTenantRedirectContext(tenant) {
  const slug = getTenantSlug(tenant);
  const origin = normalizeStoredOrigin(resolveTenantAppOrigin(tenant));

  if (slug) {
    writeStorage(TENANT_SLUG_STORAGE_KEY, slug);
  }

  if (!origin || typeof window === 'undefined') return;

  try {
    const currentHost = window.location.host;
    const originHost = new URL(origin).host;
    const isTenantLikeOrigin =
      (slug && hostStartsWithSlug(originHost, slug)) ||
      originHost !== currentHost ||
      hasDistinctSubdomain(originHost);

    if (isTenantLikeOrigin) {
      writeStorage(TENANT_APP_ORIGIN_STORAGE_KEY, origin);
    }
  } catch {
    /* ignore invalid URL */
  }
}

export function clearStoredTenantRedirectContext() {
  writeStorage(TENANT_APP_ORIGIN_STORAGE_KEY, '');
  writeStorage(TENANT_SLUG_STORAGE_KEY, '');
}

export function buildStoredTenantAppUrl(pathname, search = '', hash = '') {
  const origin = getStoredTenantAppOrigin();
  if (!origin) return '';
  const path = String(pathname || '/');
  return `${origin}${path}${String(search || '')}${String(hash || '')}`;
}

export function rewriteUrlToTenantOrigin(href, tenant) {
  if (!href || typeof window === 'undefined') return href;

  if (shouldUseLocalDevTenantFallback()) {
    try {
      const targetUrl = new URL(String(href), window.location.origin);
      const preferredOrigin = resolveLocalDevPreferredOrigin(targetUrl);
      return rewriteUrlToOrigin(targetUrl.toString(), preferredOrigin);
    } catch {
      return href;
    }
  }

  const tenantOrigin = resolveTenantAppOrigin(tenant);
  if (!tenantOrigin) return href;

  try {
    const targetUrl = new URL(String(href), window.location.origin);
    const preferredUrl = new URL(tenantOrigin);
    const slug = getTenantSlug(tenant);

    if (targetUrl.origin === preferredUrl.origin) {
      return targetUrl.toString();
    }

    if (!slug && targetUrl.origin !== window.location.origin) {
      return targetUrl.toString();
    }

    targetUrl.protocol = preferredUrl.protocol;
    targetUrl.host = preferredUrl.host;
    return targetUrl.toString();
  } catch {
    return href;
  }
}

export function buildTenantDjangoDashboardUrl(tenant, lang) {
  const localDevApiOrigin = getLocalDevTenantApiOrigin();
  if (localDevApiOrigin) {
    return `${localDevApiOrigin}${buildLocalizedPath('/dashboard/', lang)}`;
  }

  const origin = resolveTenantAppOrigin(tenant);
  if (!origin) return '';
  return `${origin}${buildLocalizedPath('/dashboard/', lang)}`;
}

export function buildTenantSpaDashboardUrl(tenant, lang) {
  const localDevSpaOrigin = getLocalDevTenantSpaOrigin();
  if (localDevSpaOrigin) {
    return `${localDevSpaOrigin}${buildLocalizedPath('/dashboard', lang)}`;
  }

  const origin = resolveTenantAppOrigin(tenant);
  if (!origin) return '';
  return `${origin}${buildLocalizedPath('/dashboard', lang)}`;
}
